import {
  Avatar,
  Flex,
  Input,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Search } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { CategoryEnum } from 'src/api/main'
import { MonoValue, ProTableContainer } from 'src/components'
import { useDebouncedValue } from 'src/hooks'

import {
  isOwnerRole,
  PERMISSION_CATEGORY_ICON,
  pickRoleColor
} from './constants'
import type { EditableWorkspaceRole, WorkspacePermissionCatalog } from './types'
import {
  groupPermissions,
  permissionMatchesQuery,
  readPermissionText,
  toBaseLang
} from './utils'

const { Text } = Typography

const CATALOG_PAGE_SIZE = 25

type Props = {
  permissions: WorkspacePermissionCatalog[]
  roles: EditableWorkspaceRole[]
}

/**
 * The workspace permission catalog. Read-only: the backend derives the list
 * from its own enum, so there is nothing to add, rename or delete here — the
 * table exists so admins can look up what a role can be granted.
 */
export const PermissionsCatalogTab = ({ permissions, roles }: Props) => {
  const { t, i18n } = useTranslation()
  const lang = toBaseLang(i18n.language)

  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<CategoryEnum | undefined>()
  const debounced = useDebouncedValue(search.trim().toLowerCase(), 200)

  const categoryLabel = useCallback(
    (value: CategoryEnum) =>
      t(`workspace.orgRoles.permission.categories.${value}`),
    [t]
  )

  const categoryOption = useCallback((value: CategoryEnum, label: string) => {
    const Icon = PERMISSION_CATEGORY_ICON[value]

    return (
      <Flex align="center" gap={6}>
        <Icon size={13} color="var(--ant-color-text-tertiary)" />
        {label}
      </Flex>
    )
  }, [])

  // Grouping drives both the category filter's options and the row order, so
  // the flat table still reads category-by-category.
  const groups = useMemo(
    () => groupPermissions(permissions, categoryLabel),
    [categoryLabel, permissions]
  )

  const visiblePermissions = useMemo(
    () =>
      groups
        .filter((group) => !category || group.key === category)
        .flatMap((group) => group.permissions)
        .filter((p) => permissionMatchesQuery(p, debounced, lang)),
    [category, debounced, groups, lang]
  )

  // Owner and admin roles hold every workspace permission implicitly; every
  // other role — built-in ones included — carries an explicit set.
  const rolesUsingPermission = (permissionId: number) =>
    roles.filter((role) =>
      isOwnerRole(role.codename) || role.is_admin
        ? true
        : role.permissions.some((p) => p.id === permissionId)
    )

  const columns: ColumnsType<WorkspacePermissionCatalog> = [
    {
      title: t('workspace.orgRoles.permission.labelLabel'),
      dataIndex: 'label',
      key: 'label',
      fixed: 'left',
      render: (_, record) => {
        const label = readPermissionText(record.label, lang) || record.code
        const description = readPermissionText(record.description, lang)

        return (
          <Flex vertical style={{ minWidth: 0, lineHeight: 1.35 }}>
            <Text strong style={{ fontSize: 13 }}>
              {label}
            </Text>
            {description ? (
              <Text
                type="secondary"
                style={{ fontSize: 11 }}
                ellipsis={{ tooltip: description }}
              >
                {description}
              </Text>
            ) : null}
          </Flex>
        )
      }
    },
    {
      title: t('workspace.orgRoles.permission.codeLabel'),
      dataIndex: 'code',
      key: 'code',
      width: 220,
      render: (code: string) => <MonoValue ellipsis>{code}</MonoValue>
    },
    {
      title: t('workspace.orgRoles.permission.categoryLabel'),
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (value: CategoryEnum) => {
        const Icon = PERMISSION_CATEGORY_ICON[value]

        return (
          <Tag
            icon={<Icon size={11} />}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            {categoryLabel(value)}
          </Tag>
        )
      }
    },
    {
      title: t('workspace.orgRoles.permission.usedByRoles'),
      key: 'roles',
      width: 170,
      render: (_, record) => {
        const using = rolesUsingPermission(record.id)

        return (
          <Flex align="center" gap={6}>
            <Avatar.Group size={20} max={{ count: 3 }}>
              {using.map((role) => {
                const color = pickRoleColor(role.codename || role.id)

                return (
                  <Tooltip key={role.id} title={role.name}>
                    <Avatar
                      size={20}
                      shape="square"
                      style={{
                        background: `${color}14`,
                        color,
                        fontSize: 9,
                        fontWeight: 600,
                        border: '1.5px solid #fff'
                      }}
                    >
                      {role.name.slice(0, 1).toUpperCase()}
                    </Avatar>
                  </Tooltip>
                )
              })}
            </Avatar.Group>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('workspace.orgRoles.permission.rolesUsing', {
                count: using.length
              })}
            </Text>
          </Flex>
        )
      }
    }
  ]

  return (
    <ProTableContainer<WorkspacePermissionCatalog>
      showSearch={false}
      rowKey="id"
      bordered
      columns={columns}
      dataSource={visiblePermissions}
      pagination={{
        pageSize: CATALOG_PAGE_SIZE,
        hideOnSinglePage: true,
        showSizeChanger: false
      }}
      locale={{ emptyText: t('workspace.orgRoles.permission.empty') }}
      leftChildren={
        <Space vertical size={2}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t('workspace.orgRoles.permission.catalogTitle')}
          </Typography.Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('workspace.orgRoles.permission.catalogDescription')}
          </Text>
        </Space>
      }
      rightChildren={
        <Flex gap={8} wrap="wrap">
          <Select
            size="small"
            style={{ width: 180 }}
            allowClear
            value={category}
            onChange={(value) => setCategory(value)}
            placeholder={t('workspace.orgRoles.permission.allCategories')}
            options={groups.map((group) => ({
              value: group.key,
              label: categoryOption(group.key, group.label)
            }))}
          />
          <Input
            size="small"
            prefix={<Search size={14} />}
            placeholder={t('workspace.orgRoles.searchPermissions')}
            style={{ width: 220 }}
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Flex>
      }
    />
  )
}
